import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FormControlLabel, Grid, Switch, Typography, Box } from '@mui/material';
import { Card, TextField, Button } from '@totalsoft/rocket-ui';
import { LinkWrapper } from '../InsuranceStyles';
import { get, set } from '@totalsoft/change-tracking-react';
import { emptyString } from 'constants/emptyConst';
import { onCheckBoxChange } from 'utils/propertyChangeAdapters';
import { RightsContext } from 'providers/RightsContext';
import { useInsuranceTypeConfiguration } from 'utils/hooks/useInsuranceTypeConfiguration';

const NegotiationCard = ({ lens, onNegotiationChecked, onOpenRequiredDocuments, onConfirmNegotiation, insuranceTypeCode, readOnly }) => {
  const { t } = useTranslation();

  const { isImage } = useContext(RightsContext);
  const insuranceTypeConfig = useInsuranceTypeConfiguration();

  // #region Memos

  const cascoInsuranceTypeCode = useMemo(() => insuranceTypeConfig.casco?.propertyCode, [insuranceTypeConfig]);

  const checkingRequiredDocuments = useMemo(
    () => (lens.mandatoryRequiredDocuments |> get) === null && (lens.optionalRequiredDocuments |> get) === null,
    [lens]
  );

  const mandatoryDocuments = useMemo(() => lens.mandatoryRequiredDocuments |> get, [lens]);
  const needsMandatoryDocuments = useMemo(() => mandatoryDocuments && mandatoryDocuments.length > 0, [mandatoryDocuments]);

  const optionalDocuments = useMemo(() => lens.optionalRequiredDocuments |> get, [lens]);
  const needsOptionalDocuments = useMemo(() => optionalDocuments && optionalDocuments.length > 0, [optionalDocuments]);

  const clientId = useMemo(() => lens.clientId |> get, [lens]);

  const selectedQuotationsCaption = useMemo(() => (lens.selectedQuotations |> get).map(q => q.name).join(', '), [lens]);
  const noSelectedQuotations = useMemo(() => (lens.selectedQuotations |> get).length < 1, [lens]);

  const negotiationAreaDisabled = useMemo(
    () =>
      noSelectedQuotations ||
      readOnly ||
      checkingRequiredDocuments ||
      needsMandatoryDocuments === true ||
      needsOptionalDocuments === true ||
      (lens.documentVerificationError |> get) ||
      !clientId,
    [checkingRequiredDocuments, clientId, needsMandatoryDocuments, needsOptionalDocuments, lens, noSelectedQuotations, readOnly]
  );

  // #endregion

  return (
    <Card title={t('Offer.Insurance.Negotiation')}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                id={'negotiationCheck'}
                color={'primary'}
                checked={(lens.negotiationChecked |> get) || false}
                onChange={onNegotiationChecked |> onCheckBoxChange}
                size='small'
                disabled={readOnly}
              />
            }
            label={t('Offer.Insurance.RequestNegotiatedOffer')}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            id={'negotiationReason'}
            fullWidth
            value={(lens.negotiationReason |> get) || emptyString}
            label={t('Offer.Insurance.NegotiationReason')}
            disabled={negotiationAreaDisabled}
            onChange={lens.negotiationReason |> set}
          />
        </Grid>
        {!isImage && (
          <Grid item xs={6} container justifyContent='flex-end'>
            <Button
              id={'negotiation_confirmBtn'}
              color={'primary'}
              disabled={negotiationAreaDisabled || !(lens.negotiationReason |> get)}
              onClick={onConfirmNegotiation}
            >
              {t('Offer.Insurance.ConfirmNegotiation')}
            </Button>
          </Grid>
        )}
        {(lens.negotiationChecked |> get) && (
          <Grid item xs={12}>
            <Typography variant='caption'>
              {(lens.selectedQuotations |> get).length > 0 && insuranceTypeCode === cascoInsuranceTypeCode && (
                <span>
                  {(lens.selectedQuotations |> get).length === 1
                    ? t('Offer.Insurance.SelectedQuotationForNegotiation', {
                        data: selectedQuotationsCaption
                      })
                    : t('Offer.Insurance.SelectedQuotationsForNegotiation', {
                        data: selectedQuotationsCaption
                      })}
                </span>
              )}
              {needsMandatoryDocuments && (
                <span>
                  {t('Offer.Insurance.MandatoryRequiredDocuments', {
                    data: mandatoryDocuments.join(', ')
                  })}
                </span>
              )}
              {needsOptionalDocuments && (
                <span>
                  {t('Offer.Insurance.OptionalRequiredDocuments', {
                    data: optionalDocuments.join(', ')
                  })}
                </span>
              )}
              {(needsMandatoryDocuments || needsOptionalDocuments) && (
                <span>
                  <LinkWrapper onClick={onOpenRequiredDocuments}>{t('Offer.Insurance.UploadRequiredDocuments')}</LinkWrapper>
                </span>
              )}
              {(lens.documentVerificationError |> get) && (
                <Box fontWeight={'fontWeightBold'}>
                  <span>{t('Offer.Insurance.DocumentVerificationError')}</span>
                </Box>
              )}
              {!checkingRequiredDocuments && !clientId && <span>{t('Offer.Insurance.ClientIsMissing')}</span>}
              {!checkingRequiredDocuments && noSelectedQuotations && <span>{t('Offer.Insurance.NoQuotationSelected')}</span>}
            </Typography>
          </Grid>
        )}
      </Grid>
    </Card>
  );
};

NegotiationCard.propTypes = {
  lens: PropTypes.object.isRequired,
  onNegotiationChecked: PropTypes.func.isRequired,
  onOpenRequiredDocuments: PropTypes.func.isRequired,
  onConfirmNegotiation: PropTypes.func.isRequired,
  insuranceTypeCode: PropTypes.string.isRequired,
  readOnly: PropTypes.bool.isRequired
};

export default NegotiationCard;
